import {Box, Button, createStyles, Group, Loader, Stack, Text, Tooltip, UnstyledButton} from '@mantine/core';
import React, {useCallback} from 'react';

import {assertAndContinue} from 'Asserts';
import {RoutineExecutionKind, RoutineExecutionStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {openChat} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import fuzzydate from 'util/fuzzydate';
import {assertNever} from 'util/Types';

import {TriggerType} from './RoutineTypes';
import {AgentHomepageRoutineExecution} from './useAgentHomepageRoutineExecutions';
import useRoutineExecutions, {RoutineExecution} from './useRoutineExecutions';

type RoutineRun = RoutineExecution | AgentHomepageRoutineExecution;

const useStyles = createStyles((theme) => ({
  row: {
    display: 'block',
    width: '100%',
    padding: theme.spacing.xs,
    borderRadius: theme.radius.sm,
    textAlign: 'left',
  },
  clickableRow: {
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  icon: {
    color: theme.colors.gray[6],
    marginTop: 2,
  },
  warningIcon: {
    color: theme.colors.orange[7],
  },
  timestamp: {
    whiteSpace: 'nowrap',
  },
}));

function getFailureReason(status: RoutineExecutionStatus): string {
  switch (status) {
    case RoutineExecutionStatus.FAILED:
      return 'The run failed before it could be dispatched.';
    case RoutineExecutionStatus.RATE_LIMITED:
      return 'The run was rate limited.';
    case RoutineExecutionStatus.PENDING:
      return 'The run has not been dispatched yet.';
    default:
      return 'The run was not dispatched.';
  }
}

function getTriggerLabel(triggerType: TriggerType): string {
  return triggerType === TriggerType.EVENT_BASED ? 'Event based' : 'Time based';
}

function getRunKindLabel(
  executionKind: RoutineExecutionKind | null | undefined,
  triggerType: TriggerType
): string {
  switch (executionKind) {
    case RoutineExecutionKind.SCHEDULED:
      return 'Time-based trigger';
    case RoutineExecutionKind.EVENT:
      return 'Event-based trigger';
    case RoutineExecutionKind.MANUAL_RUN:
      return 'Run now';
    case RoutineExecutionKind.USER_MESSAGE:
      return 'Direct message';
    case RoutineExecutionKind.AGENT_MESSAGE:
      return 'Agent message';
    case null:
    case undefined:
      return getTriggerLabel(triggerType);
    default:
      assertAndContinue(false, `Unexpected routine execution kind: ${assertNever(executionKind)}`);
      return getTriggerLabel(triggerType);
  }
}

interface RunRowProps {
  run: RoutineRun;
  routineName: string;
  showExecutionKind: boolean;
  triggerType: TriggerType;
}

function RunRow({run, routineName, showExecutionKind, triggerType}: RunRowProps): JSX.Element {
  const {classes, cx} = useStyles();
  const chatSessionId = run.chatSession?.id ?? null;
  const wasDispatched = run.status === RoutineExecutionStatus.DISPATCHED;
  const canViewChat = wasDispatched && chatSessionId != null && APIUtil.isChatSession(chatSessionId);
  const executionKind = 'executionKind' in run ? run.executionKind : undefined;
  const dispatchedIcon =
    showExecutionKind && executionKind !== RoutineExecutionKind.USER_MESSAGE
      ? 'robot-outline'
      : 'comment-outline';

  const handleOpenChat = useCallback(() => {
    if (chatSessionId != null) {
      openChat(chatSessionId);
    }
  }, [chatSessionId]);

  const content = (
    <Group position="apart" noWrap={true} align="flex-start" spacing="sm">
      <Group spacing="sm" noWrap={true} align="flex-start">
        {wasDispatched ? (
          <BenchlingIcon icon={dispatchedIcon} className={classes.icon} />
        ) : (
          <Tooltip label={getFailureReason(run.status)} multiline={true} withArrow={true}>
            <Box component="span" role="img" aria-label="Run warning">
              <BenchlingIcon icon="exclamation-triangle" className={cx(classes.icon, classes.warningIcon)} />
            </Box>
          </Tooltip>
        )}
        <Stack spacing={0}>
          <Text size="sm" fw={500} lineClamp={1}>
            {routineName}
          </Text>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {showExecutionKind ? getRunKindLabel(executionKind, triggerType) : getTriggerLabel(triggerType)}
          </Text>
        </Stack>
      </Group>
      <Text size="xs" c="dimmed" className={classes.timestamp}>
        {fuzzydate(run.createdAt, {compact: true})}
      </Text>
    </Group>
  );

  if (canViewChat) {
    return (
      <UnstyledButton className={cx(classes.row, classes.clickableRow)} onClick={handleOpenChat}>
        {content}
      </UnstyledButton>
    );
  }
  return <Box className={classes.row}>{content}</Box>;
}

interface RoutineRunsTabProps {
  routineId: string;
  routineName: string;
  triggerType: TriggerType;
}

interface RoutineRunsListProps {
  runs: ReadonlyArray<RoutineRun>;
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  loadMore: () => void;
  routineName: string;
  showExecutionKind?: boolean;
  triggerType: TriggerType;
}

export function RoutineRunsList({
  runs,
  isLoading,
  isError,
  hasMore,
  loadMore,
  routineName,
  showExecutionKind = false,
  triggerType,
}: RoutineRunsListProps): JSX.Element {
  if (isLoading && runs.length === 0) {
    return (
      <Group position="center" py="lg">
        <Loader size="sm" />
      </Group>
    );
  }

  if (isError && runs.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        Failed to load runs. Please try again.
      </Text>
    );
  }

  if (runs.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        This agent hasn't run yet.
      </Text>
    );
  }

  return (
    <Stack spacing="md">
      <Stack spacing={4}>
        {runs.map((run) => (
          <RunRow
            key={run.id}
            run={run}
            routineName={routineName}
            showExecutionKind={showExecutionKind}
            triggerType={triggerType}
          />
        ))}
      </Stack>
      {hasMore && (
        <Group position="center">
          <Button variant="ghost" size="sm" onClick={loadMore}>
            Load more
          </Button>
        </Group>
      )}
    </Stack>
  );
}

export default function RoutineRunsTab({
  routineId,
  routineName,
  triggerType,
}: RoutineRunsTabProps): JSX.Element {
  const {runs, isLoading, isError, hasMore, loadMore} = useRoutineExecutions({routineId});

  return (
    <RoutineRunsList
      runs={runs}
      isLoading={isLoading}
      isError={isError}
      hasMore={hasMore}
      loadMore={loadMore}
      routineName={routineName}
      triggerType={triggerType}
    />
  );
}
